// Pantheon Visual Intelligence Center — pure scene model.
//
// All 3D math for the visualization lives here as pure, deterministic
// functions: node layout, the Pantheon core cluster, connection geometry,
// dataflow pulses, camera focus, and quality tiers. No DOM, no canvas, no
// timers — the renderer consumes this model, tests exercise it directly.
//
// Determinism: a seeded PRNG (mulberry32) drives every "organic" placement and
// pulse schedule, so the same inputs always produce the same scene.

// --------------------------------------------------------------------------
// Seeded PRNG
// --------------------------------------------------------------------------

export function mulberry32(seed) {
  let a = seed >>> 0;
  return function next() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const SCENE_SEED = 20260715;

// --------------------------------------------------------------------------
// Quality tiers — responsive complexity control
// --------------------------------------------------------------------------

export function qualityForViewport({ width = 1280, reducedMotion = false } = {}) {
  if (reducedMotion) {
    return {
      key: "static",
      corePoints: 150,
      coreFilaments: 110,
      dustPoints: 36,
      maxPulsesPerLink: 0,
      ambientPulseIntervalMs: Infinity,
      autoRotateSpeed: 0,
      showCrossLinksDefault: width >= 900,
    };
  }
  if (width < 640) {
    return {
      key: "compact",
      corePoints: 120,
      coreFilaments: 80,
      dustPoints: 30,
      maxPulsesPerLink: 1,
      ambientPulseIntervalMs: 5200,
      autoRotateSpeed: 0.018,
      showCrossLinksDefault: false,
    };
  }
  if (width < 1024) {
    return {
      key: "balanced",
      corePoints: 180,
      coreFilaments: 130,
      dustPoints: 60,
      maxPulsesPerLink: 2,
      ambientPulseIntervalMs: 4200,
      autoRotateSpeed: 0.022,
      showCrossLinksDefault: true,
    };
  }
  return {
    key: "full",
    corePoints: 260,
    coreFilaments: 200,
    dustPoints: 90,
    maxPulsesPerLink: 3,
    ambientPulseIntervalMs: 3400,
    autoRotateSpeed: 0.026,
    showCrossLinksDefault: true,
  };
}

// --------------------------------------------------------------------------
// Scene construction
// --------------------------------------------------------------------------

// Cinematic scale: the intelligence ring orbits at twice its original radius
// and the camera sits farther back with a higher base zoom, so the network
// fills roughly 65–75% of the canvas width while the core stays central.
const NODE_RING_RADIUS = 2;
const CORE_RADIUS = 0.34;

// Base camera magnification applied to the whole scene (focus states multiply
// on top of it).
export const BASE_ZOOM = 1.18;

function buildNodeLayout(nodes) {
  const count = nodes.length || 1;
  return nodes.map((node, index) => {
    const angle = (index / count) * Math.PI * 2 - Math.PI / 2;
    // Deterministic vertical + radial variation keeps the ring dimensional
    // without reading as a random particle cloud.
    const wobbleY = Math.sin(index * 2.39996) * 0.16;
    const radius = NODE_RING_RADIUS * (1 + (index % 2 === 0 ? 0.035 : -0.035));
    return {
      id: node.id,
      angle,
      position: {
        x: Math.cos(angle) * radius,
        y: wobbleY,
        z: Math.sin(angle) * radius,
      },
    };
  });
}

function buildCoreCluster(quality, rng) {
  const points = [];
  for (let i = 0; i < quality.corePoints; i += 1) {
    // Rejection-free spherical distribution biased toward the center so the
    // cluster reads dense-in-the-middle like a living intelligence core.
    const u = rng();
    const v = rng();
    const theta = u * Math.PI * 2;
    const phi = Math.acos(2 * v - 1);
    const r = CORE_RADIUS * Math.cbrt(rng()) * (0.55 + 0.45 * rng());
    points.push({
      x: r * Math.sin(phi) * Math.cos(theta),
      y: r * Math.sin(phi) * Math.sin(theta) * 0.82,
      z: r * Math.cos(phi),
      size: 0.35 + rng() * 0.85,
      twinklePhase: rng() * Math.PI * 2,
    });
  }

  // Filaments connect nearby points (bounded search keeps this O(n·k)).
  const filaments = [];
  const maxFilaments = quality.coreFilaments;
  for (let i = 0; i < points.length && filaments.length < maxFilaments; i += 1) {
    const a = points[i];
    let best = -1;
    let bestDist = Infinity;
    const span = Math.min(points.length, i + 24);
    for (let j = i + 1; j < span; j += 1) {
      const b = points[j];
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const dz = a.z - b.z;
      const d = dx * dx + dy * dy + dz * dz;
      if (d < bestDist) {
        bestDist = d;
        best = j;
      }
    }
    if (best >= 0 && bestDist < CORE_RADIUS * CORE_RADIUS * 0.4) {
      filaments.push({ a: i, b: best });
    }
  }

  return { points, filaments };
}

function buildDust(quality, rng) {
  const dust = [];
  for (let i = 0; i < quality.dustPoints; i += 1) {
    dust.push({
      x: (rng() * 2 - 1) * 4.2,
      y: (rng() * 2 - 1) * 2.2,
      z: (rng() * 2 - 1) * 4.2,
      size: 0.2 + rng() * 0.5,
      twinklePhase: rng() * Math.PI * 2,
    });
  }
  return dust;
}

function controlPointFor(from, to, lift) {
  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2;
  const mz = (from.z + to.z) / 2;
  const mag = Math.sqrt(mx * mx + mz * mz) || 1;
  const push = 1 + lift;
  return {
    x: (mx / mag) * mag * push,
    y: my + 0.14,
    z: (mz / mag) * mag * push,
  };
}

export function createScene(viewModel, quality) {
  const rng = mulberry32(SCENE_SEED);
  const layout = buildNodeLayout(viewModel.nodes);
  const layoutById = new Map(layout.map((entry) => [entry.id, entry]));
  const origin = { x: 0, y: 0, z: 0 };

  const primaryLinks = viewModel.nodes.map((node) => {
    const nodeLayout = layoutById.get(node.id);
    return {
      kind: "primary",
      nodeId: node.id,
      color: node.color,
      flowKey: node.flow.key,
      activityLevel: node.activityLevel,
      from: origin,
      to: nodeLayout.position,
      control: controlPointFor(origin, nodeLayout.position, 0.08),
    };
  });

  const crossLinks = viewModel.crossLinks.map((link) => {
    const fromLayout = layoutById.get(link.from);
    const toLayout = layoutById.get(link.to);
    const fromNode = viewModel.nodes.find((n) => n.id === link.from);
    return {
      kind: "cross",
      nodeId: link.to,
      sourceId: link.from,
      label: link.label,
      linkType: link.kind,
      color: fromNode ? fromNode.color : "#889ce7",
      from: fromLayout.position,
      to: toLayout.position,
      control: controlPointFor(fromLayout.position, toLayout.position, 0.28),
    };
  });

  return {
    quality,
    layout,
    layoutById,
    core: buildCoreCluster(quality, rng),
    dust: buildDust(quality, rng),
    primaryLinks,
    crossLinks,
    state: {
      yaw: 0.6,
      pitch: -0.32,
      zoom: BASE_ZOOM,
      targetYaw: null,
      targetZoom: BASE_ZOOM,
      breathePhase: 0,
      timeMs: 0,
      pulses: [],
      flashes: {},
      pulseAccumulators: primaryLinks.map(() => rng() * 4000),
      focusedId: null,
      rng,
    },
  };
}

// --------------------------------------------------------------------------
// Camera focus
// --------------------------------------------------------------------------

// Rotate so the focused node lands front-center: z' = R·sin(angle + yaw) is
// minimal when angle + yaw = -π/2.
export function focusScene(scene, focusId) {
  const { state } = scene;
  if (!focusId || focusId === "pantheon") {
    state.focusedId = focusId || null;
    state.targetYaw = null;
    state.targetZoom = focusId === "pantheon" ? BASE_ZOOM * 1.16 : BASE_ZOOM;
    return scene;
  }
  const layout = scene.layoutById.get(focusId);
  if (!layout) return scene;
  state.focusedId = focusId;
  state.targetYaw = -Math.PI / 2 - layout.angle;
  state.targetZoom = BASE_ZOOM * 1.26;
  return scene;
}

export function resetSceneFocus(scene) {
  scene.state.focusedId = null;
  scene.state.targetYaw = null;
  scene.state.targetZoom = BASE_ZOOM;
  return scene;
}

// --------------------------------------------------------------------------
// Animation step
// --------------------------------------------------------------------------

function normalizeAngleDelta(delta) {
  let d = delta;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}

export function advanceScene(scene, dtMs, { paused = false } = {}) {
  const { state, quality } = scene;
  const dt = Math.min(Math.max(dtMs, 0), 100); // clamp long frames
  state.timeMs += dt;

  if (!paused) {
    state.breathePhase += dt * 0.00045;

    if (state.targetYaw !== null) {
      const delta = normalizeAngleDelta(state.targetYaw - state.yaw);
      state.yaw += delta * Math.min(1, dt * 0.004);
    } else {
      state.yaw += quality.autoRotateSpeed * (dt / 1000);
    }
    state.zoom += (state.targetZoom - state.zoom) * Math.min(1, dt * 0.004);

    // Pulse spawning: ambient baseline (visualization-only) plus a boost from
    // the node's derived activity level. Interval Infinity disables spawning.
    if (quality.ambientPulseIntervalMs !== Infinity) {
      scene.primaryLinks.forEach((link, index) => {
        state.pulseAccumulators[index] += dt;
        const interval =
          quality.ambientPulseIntervalMs / (1 + link.activityLevel * 2.5);
        if (state.pulseAccumulators[index] >= interval) {
          state.pulseAccumulators[index] = state.rng() * 600;
          const active = state.pulses.filter((p) => p.linkIndex === index).length;
          if (active < quality.maxPulsesPerLink) {
            state.pulses.push({
              linkIndex: index,
              kind: "primary",
              t: 0,
              speed: 0.00016 + state.rng() * 0.00014,
              outbound: state.rng() > 0.45,
              size: 0.8 + state.rng() * 0.8,
            });
          }
        }
      });
    }

    // Advance pulses; flash the receiving endpoint on arrival.
    const surviving = [];
    for (let i = 0; i < state.pulses.length; i += 1) {
      const pulse = state.pulses[i];
      pulse.t += pulse.speed * dt;
      if (pulse.t >= 1) {
        const link = scene.primaryLinks[pulse.linkIndex];
        if (link) {
          const target = pulse.outbound ? link.nodeId : "pantheon";
          state.flashes[target] = 1;
        }
      } else {
        surviving.push(pulse);
      }
    }
    state.pulses = surviving;
  }

  // Flash decay runs even while paused so highlights settle gracefully.
  Object.keys(state.flashes).forEach((key) => {
    const next = state.flashes[key] - dt * 0.0016;
    if (next <= 0) delete state.flashes[key];
    else state.flashes[key] = next;
  });

  return scene;
}

// --------------------------------------------------------------------------
// Projection
// --------------------------------------------------------------------------

export const CAMERA_DISTANCE = 4.6;
const FOCAL_LENGTH = 2.6;

// Pixel scale of one scene unit at camera depth. Width-led (with a height
// cap) so the doubled ring reads at ~65–75% of the canvas width without
// clipping side nodes or their offset labels on any aspect ratio.
export function viewportUnit(viewport) {
  return Math.min(viewport.width * 0.27, viewport.height * 0.58);
}

// Perspective-project a scene-space point into viewport pixels.
export function projectPoint(point, state, viewport) {
  const cosY = Math.cos(state.yaw);
  const sinY = Math.sin(state.yaw);
  const cosP = Math.cos(state.pitch);
  const sinP = Math.sin(state.pitch);

  const x1 = point.x * cosY - point.z * sinY;
  const z1 = point.x * sinY + point.z * cosY;
  const y1 = point.y * cosP - z1 * sinP;
  const z2 = point.y * sinP + z1 * cosP;

  const depth = z2 + CAMERA_DISTANCE;
  const scale = (FOCAL_LENGTH / Math.max(depth, 0.4)) * state.zoom;
  const unit = viewportUnit(viewport);

  return {
    x: viewport.width / 2 + x1 * scale * unit,
    y: viewport.height / 2 + y1 * scale * unit,
    scale,
    depth,
  };
}

// --------------------------------------------------------------------------
// Viewport containment
// --------------------------------------------------------------------------

// Node anchors are clamped into these safe bounds every frame so ambient
// rotation, focus zoom, and extreme aspect ratios can never push a node's
// marker, orbital ring, or shells outside the stage (which is
// overflow:hidden). Bounds derive only from the live viewport — never from
// hard-coded screen sizes — and are deliberately label-independent: label
// room is always reserved (labels flip inward within these clearances), so
// toggling Labels can never move a node.
export const NODE_CLEARANCE = 48; // marker + orbital ring + counter-rotated shells
export const CONTROL_BAR_CLEARANCE = 88; // bottom scene-control strip + its offset

export function safeNodeBounds(viewport, { hasControls = true } = {}) {
  const width = Math.max(viewport.width || 0, 0);
  const height = Math.max(viewport.height || 0, 0);
  const minX = NODE_CLEARANCE;
  const minY = NODE_CLEARANCE;
  const bottomClearance = hasControls ? CONTROL_BAR_CLEARANCE : 0;
  return {
    minX,
    maxX: Math.max(minX, width - NODE_CLEARANCE),
    minY,
    maxY: Math.max(minY, height - bottomClearance - NODE_CLEARANCE),
  };
}

// Ambient motion comes from camera rotation, not per-node velocities, so
// containment clamps the projected anchor: the underlying (unclamped) anchor
// keeps rotating, which makes a boundary node slide along the edge and
// re-enter smoothly — there is no velocity feedback and therefore no jitter.
export function clampPointToBounds(point, bounds) {
  const x = Math.min(Math.max(point.x, bounds.minX), bounds.maxX);
  const y = Math.min(Math.max(point.y, bounds.minY), bounds.maxY);
  if (x === point.x && y === point.y) return point;
  return { ...point, x, y };
}

// Label placement for a projected node anchor. Labels prefer to push outward
// from the scene center (so edge-on clusters never stack their text), but
// placement flips back toward the interior when the anchor sits within a
// label's reach of a container edge — the stage is overflow:hidden, and edge
// proximity must reposition a label, never clip it away. The bottom reach is
// larger than the top: it must clear the label card itself plus the scene
// control bar, so labels never slide underneath the controls. Placement is a
// pure positioning concern: it must never decide whether a label exists.
const LABEL_REACH_X = 200;
const LABEL_REACH_TOP = 84;
const LABEL_REACH_BOTTOM = CONTROL_BAR_CLEARANCE + 84;

export function labelPlacementFor(point, viewport) {
  const { width, height } = viewport;
  const dx = point.x - width / 2;
  const dy = point.y - height / 2;
  let side = dx < -width * 0.16 ? "left" : dx > width * 0.16 ? "right" : "center";
  let vpos = dy < -height * 0.08 ? "above" : "below";
  if (point.x < LABEL_REACH_X) side = "right";
  else if (point.x > width - LABEL_REACH_X) side = "left";
  if (point.y < LABEL_REACH_TOP) vpos = "below";
  else if (point.y > height - LABEL_REACH_BOTTOM) vpos = "above";
  return { side, vpos };
}

// Point along a quadratic bezier in scene space (for pulse positions).
export function pointOnLink(link, t) {
  const mt = 1 - t;
  return {
    x: mt * mt * link.from.x + 2 * mt * t * link.control.x + t * t * link.to.x,
    y: mt * mt * link.from.y + 2 * mt * t * link.control.y + t * t * link.to.y,
    z: mt * mt * link.from.z + 2 * mt * t * link.control.z + t * t * link.to.z,
  };
}

const pantheonSceneModel = {
  mulberry32,
  qualityForViewport,
  createScene,
  focusScene,
  resetSceneFocus,
  advanceScene,
  projectPoint,
  pointOnLink,
  labelPlacementFor,
  safeNodeBounds,
  clampPointToBounds,
  viewportUnit,
  BASE_ZOOM,
  CAMERA_DISTANCE,
};

export default pantheonSceneModel;
